import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LocalLink from "@/components/ui/LocalLink";
import LuxuryCard from "@/components/ui/LuxuryCard";
import { getPostsByCategory, blogCategories } from "@/utils/blogData";
import { useSEO } from "@/utils/useSEO";

export default function NewsCategory() {
  const { t } = useTranslation();
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const category = blogCategories.find(cat => cat.slug === categorySlug);
  const categoryName = category ? category.name : "Category";

  useSEO(`News - ${categoryName}`);

  const posts = categorySlug ? getPostsByCategory(categorySlug) : [];

  return (
    <>
      {/* Simple Header — no fullscreen hero */}
      <div className="pt-32 pb-12 bg-[#0B0B0B] border-b border-white/5">
        <div className="container mx-auto px-6 max-w-6xl">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-white/40 mb-6">
            <LocalLink to="/news" className="hover:text-gold transition-colors">{t("menu.news", "News")}</LocalLink>
            <span>/</span>
            <span className="text-white/70">{categoryName}</span>
          </div>

          <span className="text-gold uppercase tracking-[0.3em] text-xs font-semibold mb-3 block">{t("newsCategory.category", "Category")}</span>
          <h1 className="text-4xl md:text-5xl font-luxury text-white mb-4">{categoryName}</h1>
          <p className="text-white/50 text-sm">
            {posts.length} {t("newsCategory.articlesInCat", "articles in this category")}
          </p>
        </div>
      </div>

      {/* Articles Grid */}
      <section className="py-16 bg-[#0B0B0B]">
        <div className="container mx-auto px-6 max-w-6xl">
          {posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/40 text-lg mb-6">{t("newsCategory.noArticles", "No articles found in this category.")}</p>
              <LocalLink to="/news" className="btn-gold px-8 py-3 rounded-full inline-block">
                {t("newsCategory.backToNews", "Back to News")}
              </LocalLink>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {posts.map((post, index) => (
                <LuxuryCard
                  key={post.id}
                  title={post.title}
                  image={post.image}
                  href={`/news/${post.slug}`}
                  description={post.excerpt}
                  features={[post.category, post.date]}
                  delay={index * 0.1}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
